/**
 * Text utilities for fuzzy matching and multi-language support
 */

/**
 * Normalize text for fuzzy matching
 * Handles accents, apostrophes, spaces, etc.
 *
 * Examples:
 * - "Télé" → "tele"
 * - "J'aime" → "jaime"
 * - "Tel Aviv" → "telaviv"
 * - "Be'er Sheva" → "beersheva"
 */
export function normalize(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')                    // Decompose: é → e + accent mark
    .replace(/[\u0300-\u036f]/g, '')    // Remove accent marks
    .replace(/[^a-zא-ת0-9]/g, '');      // Keep only letters and numbers
}

/**
 * Get label in user's current language with fallback to Hebrew
 */
export function getLabel(
  item: {
    label?: string;
    label_en?: string;
    label_fr?: string;
    label_ru?: string;
    settlement_name?: string;
    settlement_name_en?: string;
    settlement_name_fr?: string;
    settlement_name_ru?: string;
  },
  lang: string
): string {
  // For settlements (settlement_name_* columns)
  if (item.settlement_name !== undefined) {
    if (lang === 'en' && item.settlement_name_en) return item.settlement_name_en;
    if (lang === 'fr' && item.settlement_name_fr) return item.settlement_name_fr;
    if (lang === 'ru' && item.settlement_name_ru) return item.settlement_name_ru;
    return item.settlement_name || '';
  }

  // For professions/specializations (label_* columns)
  if (lang === 'en' && item.label_en) return item.label_en;
  if (lang === 'fr' && item.label_fr) return item.label_fr;
  if (lang === 'ru' && item.label_ru) return item.label_ru;
  return item.label || '';
}

/**
 * Filter items by text query with fuzzy matching across multiple language columns
 */
export function filterByText<T extends Record<string, any>>(
  items: T[],
  query: string,
  labelKeys: string[]
): T[] {
  if (!query?.trim()) return items;

  const normalizedQuery = normalize(query);

  return items.filter(item =>
    labelKeys.some(key => {
      const value = item[key];
      return value && normalize(value).includes(normalizedQuery);
    })
  );
}

/**
 * Get all searchable keys for settlements
 */
export const SETTLEMENT_SEARCH_KEYS = [
  'settlement_name',
  'settlement_name_en',
  'settlement_name_fr',
  'settlement_name_ru'
];

/**
 * Get all searchable keys for professions/specializations
 */
export const PROFESSION_SEARCH_KEYS = [
  'label',
  'label_en',
  'label_fr',
  'label_ru'
];

/**
 * Normalize Hebrew text for fuzzy matching
 * - Converts final letters to regular form (ך→כ, ם→מ, etc.)
 * - Removes punctuation and quotes
 * - Converts to lowercase
 */
export const normalizeHebrew = (text: string): string => {
  if (!text) return '';
  
  const finalLetterMap: Record<string, string> = {
    'ך': 'כ',
    'ם': 'מ',
    'ן': 'נ',
    'ף': 'פ',
    'ץ': 'צ'
  };
  
  return text
    .replace(/[ךםןףץ]/g, m => finalLetterMap[m] || m)
    .replace(/['"״׳\-–—]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .trim();
};

/**
 * Calculate similarity score between two words based on shared characters
 * Returns a value between 0 and 1
 */
const calculateSimilarity = (word1: string, word2: string): number => {
  if (word1 === word2) return 1;
  if (!word1 || !word2) return 0;
  
  // Count shared characters
  const chars1 = new Set(word1.split(''));
  const chars2 = new Set(word2.split(''));
  const intersection = [...chars1].filter(c => chars2.has(c)).length;
  
  return intersection / Math.max(chars1.size, chars2.size);
};

/**
 * Fuzzy match for Hebrew text with advanced matching:
 * - Direct inclusion check
 * - Prefix matching (3+ first letters)
 * - Similarity score for similar words (>= 60% shared chars)
 */
export const fuzzyMatch = (searchTerm: string, targetText: string): boolean => {
  if (!searchTerm || !targetText) return false;
  
  const normalizedSearch = normalizeHebrew(searchTerm);
  const normalizedTarget = normalizeHebrew(targetText);
  
  // 1. Direct match - full search term in target
  if (normalizedTarget.includes(normalizedSearch)) return true;
  
  // 2. Split into words for advanced matching
  const searchWords = normalizedSearch.split(/\s+/).filter(w => w.length >= 2);
  const targetWords = normalizedTarget.split(/\s+/).filter(w => w.length >= 2);
  
  if (searchWords.length === 0) return false;
  
  // All search words must have a match in target
  return searchWords.every(searchWord => {
    // 2a. Direct word inclusion
    if (normalizedTarget.includes(searchWord)) return true;
    
    // 2b. Prefix match - first 3 letters of search word found in target
    if (searchWord.length >= 3) {
      const prefix = searchWord.slice(0, 3);
      if (normalizedTarget.includes(prefix)) return true;
    }
    
    // 2c. Similarity match - find a similar word in target (>= 60% shared chars)
    return targetWords.some(targetWord => {
      const similarity = calculateSimilarity(searchWord, targetWord);
      return similarity >= 0.6;
    });
  });
};
